import React, { useState } from 'react';
import {
  Modal, Form, Button, Row, Col,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';
import PricingService from '../../../Services/PricingService';
import { ServiceAreaGeoEditor } from '../../../Components/PricingGeo';

function ServiceAreaModal({
  originalServiceArea = {}, show = false, handleClose, fetchServiceAreas,
}) {
  const defaultServiceArea = {
    active: 1,
    is_base_location: false,
    price_multiplier: 1.0,
    geographic_definition: {
      type: 'radius',
      center: { latitude: 47.4979, longitude: 19.0402 }, // Budapest default
      radius_km: 10,
      polygon_coordinates: [],
    },
  };

  const [serviceArea, setServiceArea] = useState(
    _.defaults(_.cloneDeep(originalServiceArea), defaultServiceArea),
  );
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const geoType = serviceArea.geographic_definition?.type;

      // Validate polygon has at least 3 points
      if (geoType === 'polygon') {
        const coords = serviceArea.geographic_definition?.polygon_coordinates || [];
        if (coords.length < 3) {
          setError('Polygon must have at least 3 points');
          setLoading(false);
          return;
        }
      }

      await PricingService.createOrUpdateServiceArea({
        ...serviceArea,
        active: Number(serviceArea.active),
        is_base_location: Boolean(serviceArea.is_base_location),
        price_multiplier: Number(serviceArea.price_multiplier || 1),
        flat_rate_eur: serviceArea.flat_rate_eur
          ? Number(serviceArea.flat_rate_eur) : undefined,
        flat_rate_huf: serviceArea.flat_rate_huf
          ? Number(serviceArea.flat_rate_huf) : undefined,
        flat_rate_applies_to: serviceArea.flat_rate_applies_to || 'origin',
        geographic_definition: {
          ...serviceArea.geographic_definition,
          radius_km: Number(serviceArea.geographic_definition?.radius_km || 10),
        },
      });
      fetchServiceAreas();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message);
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setStateFromInput(e, setServiceArea, serviceArea);
  };

  const handleGeoDefinitionChange = (nextGeoDef) => {
    setServiceArea((prev) => ({
      ...prev,
      geographic_definition: nextGeoDef,
    }));
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {serviceArea.Id ? `Edit ${serviceArea.name}` : 'New Service Area'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="name">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={serviceArea.name || ''}
                  onChange={handleChange}
                  placeholder="e.g., Budapest Airport"
                />
                <FormControlFeedback errors={validationErrors} field="name" />
              </Form.Group>

              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={serviceArea.description || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="is_base_location">
                    <Form.Label>Is Base Location?</Form.Label>
                    <Form.Select
                      value={serviceArea.is_base_location ? '1' : '0'}
                      onChange={(e) => setServiceArea((prev) => ({
                        ...prev,
                        is_base_location: e.target.value === '1',
                      }))}
                    >
                      <option value="1">Yes (Drivers dispatch from here)</option>
                      <option value="0">No (Service area only)</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Base locations are where drivers start their journeys
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="active">
                    <Form.Label>Status</Form.Label>
                    <Form.Select value={serviceArea.active} onChange={handleChange}>
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3" controlId="price_multiplier">
                <Form.Label>Price Multiplier</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={serviceArea.price_multiplier || 1}
                  onChange={handleChange}
                  min={0.1}
                />
                <Form.Text className="text-muted">
                  Multiplier applied to base price (1.0 = no change, 1.5 = 50% increase)
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <hr className="d-md-none" />
              <h5 className="mb-3">Service Area Fee (Optional)</h5>
              <p className="text-muted small">
                Add a flat fee when this area is used as origin/destination
                (e.g., airport pickup fee)
              </p>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="flat_rate_eur">
                    <Form.Label>Flat Rate (EUR)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={serviceArea.flat_rate_eur || ''}
                      onChange={handleChange}
                      min={0}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="flat_rate_huf">
                    <Form.Label>Flat Rate (HUF)</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      value={serviceArea.flat_rate_huf || ''}
                      onChange={handleChange}
                      min={0}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="flat_rate_applies_to">
                    <Form.Label>Applies When</Form.Label>
                    <Form.Select
                      value={serviceArea.flat_rate_applies_to || 'origin'}
                      onChange={handleChange}
                    >
                      <option value="origin">Origin (Pickup)</option>
                      <option value="destination">Destination (Dropoff)</option>
                      <option value="both">Both</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>

          <hr />
          <h5 className="mb-3">Geographic Definition</h5>

          <ServiceAreaGeoEditor
            value={serviceArea.geographic_definition}
            onChange={handleGeoDefinitionChange}
            active={show}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : 'Save Service Area'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ServiceAreaModal;
